import { Dropdown, FontIcon, IconButton, IDropdownProps, ILabelProps, ILinkProps, ITextFieldProps, ITooltipHostProps, Label, Link, Separator as RawSeparator, SpinButton, Stack, TextField, Toggle, TooltipHost } from '@fluentui/react';
import { CSSProperties, FC, FCWithChildren, ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { AppThemed } from '../styles/themes';
import { nameof } from '../utils/stringUtils';

export type Measure = string | number;

export function fixMeasure(value: Measure): string {
    return typeof value === 'number'
        ? `${value}px`
        : value;
}

export const FlexDiv = styled.div<{ justify?: CSSProperties['justifyContent']; align?: CSSProperties['alignItems']; width?: Measure; height?: Measure }>`
  display: flex;
  flex-direction: column;
  width: ${p => p.width && fixMeasure(p.width)};
  height: ${p => p.height && fixMeasure(p.height)};
  justify-content: ${p => p.justify ?? 'center'};
  align-items: ${p => p.align ?? 'normal'};
`;

export const StyledToggle = styled(Toggle)`
  margin-bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface ILinkButtonProps {
    children: ReactNode;
    onClick?(): void;
    href?: string;
    style?: CSSProperties;
    styles?: ILinkProps['styles'];
}

export function LinkButton({ children, onClick, href, style, styles }: ILinkButtonProps): JSX.Element {
    const nonNavigatingOnClick = useCallback<NonNullable<ILinkProps['onClick']>>(
        (e) => {
            onClick?.();

            e.preventDefault();
            return false;
        },
        [onClick],
    );

    return <Link href={href} onClick={nonNavigatingOnClick} style={style} styles={styles}>{children}</Link>;
}

const RawReadonlyTextField = styled(TextField)`
  label {
    color: inherit;
  }

  input, textarea {
    color: ${p => p.theme.palette.neutralPrimaryAlt};
  }
`;

const emptyMethod = () => { /* do nothing */ };

export const ReadonlyTextField: FC<Omit<AppThemed<ITextFieldProps>, 'disabled' | 'readonly' | 'onChange' | 'as'>> = (props) => (
    <RawReadonlyTextField
        {...props}
        disabled
        onChange={emptyMethod}
    />
);

export const CustomWidthSpinButton = styled(SpinButton)`
  &, * {
    min-width: unset;
    max-width: 100%;
  }
`;

export const IconAndTextContainer = styled.div`
  display: flex;
  align-items: center;

  span, div:not(:first-child) {
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

export const CenterContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalHeaderDiv = styled.div`
  margin-bottom: 1rem;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  font-size: 1.5rem;

  & > :first-child {
    margin-left: 0.5rem;
    margin-right: 2rem;
  }
`;

export const ModalHeader: FC<{ title: string; onCancel?(): void }> = ({ title, onCancel }) => (
    <ModalHeaderDiv>
        <div>{title}</div>
        {
            onCancel &&
            <IconButton
                title="Cancel"
                iconProps={{ iconName: 'Cancel' }}
                onClick={onCancel}
            />
        }
    </ModalHeaderDiv>
);

interface IHintTextProps {
    centered?: boolean;
    italic?: boolean;
    height?: Measure;
    verticalAlign?: CSSProperties['alignItems']
}

export const HintText = styled.div<IHintTextProps>`
  display: flex;
  flex-direction: row;
  white-space: pre-wrap;
  height: ${p => p.height && fixMeasure(p.height)};
  padding: 0.125rem 0.5rem;
  color: ${p => p.theme.palette.neutralSecondary};
  
  ${p => p.centered && css`
    justify-content: center;
    text-align: center;
  `}
  ${p => p.verticalAlign && css`align-items: ${p.verticalAlign};`}
  ${p => p.italic && css`font-style: italic;`}
`;

const HintStack = styled(Stack)`
  user-select: none;
  color: ${p => p.theme.palette.neutralSecondaryAlt};
`;

export const Hint: FCWithChildren<IHintTextProps> = (props) => (
    <HintStack horizontal horizontalAlign="center" verticalAlign="center" style={{ padding: '0.125rem 0.5rem' }}>
        <FontIcon iconName="Info" style={{ marginRight: '0.25rem' }} />
        <HintText {...props} />
    </HintStack>
);

export const ErrorText = styled.div`
  padding: 0.125rem 0.5rem;
  color: ${p => p.theme.semanticColors.errorText};
  text-align: center;
`;

export const Separator = styled(RawSeparator) <{ bold?: boolean }>`
  ::before {
    background-color: ${p => p.theme.palette.neutralQuaternary};
    ${p => p.bold && css`height: 2px;`}
  }

  div[role="separator"] {
    font-weight: ${p => p.bold && 600};
  }
`;
Separator.displayName = nameof({ Separator });

export const EditBlockerDiv: FCWithChildren<{ canEdit: boolean; noEditText?: string }> = ({ canEdit, noEditText, children }) => (
    <div
        style={!canEdit ? { pointerEvents: 'none', userSelect: 'none' } : undefined}
        onKeyDown={!canEdit ? (e) => { e.preventDefault(); return false; } : undefined}
        tabIndex={!canEdit ? -1 : undefined}
    >
        {
            (!canEdit && noEditText) &&
            <HintStack horizontal horizontalAlign="center" verticalAlign="center" style={{ padding: '0.125rem 0.5rem' }}>
                <FontIcon iconName="Info" style={{ marginRight: '0.25rem' }} />
                <span>{noEditText}</span>
            </HintStack>
        }
        {children}
    </div>
);
EditBlockerDiv.displayName = nameof({ EditBlockerDiv });

export const UnderlinedTextField = styled(TextField)`
  div[class*="ms-TextField-fieldGroup"] {
    border: 1px solid transparent;
    border-bottom: 1px solid ${p => p.theme.palette.neutralSecondary};

    ::after {
      border-color: transparent;
      border-bottom-color: ${p => p.theme.palette.themePrimary};
    }

    :hover {
      border-bottom-color: ${p => p.theme.palette.neutralPrimary};
    }
  }
`;

export const CollapsibleDiv = styled.div<{ isOpen: boolean; minHeight?: Measure; maxHeight?: Measure }>`
  transition: max-height 0.6s ease-in-out;
  overflow-y: hidden;
  max-height: ${p => p.isOpen ? (p.maxHeight ? fixMeasure(p.maxHeight) : '100%') : (p.minHeight ? fixMeasure(p.minHeight) : '0')};
`;

export const ListDiv = styled.div.attrs({ 'data-is-scrollable': 'true' }) <{ flexGrow?: boolean | number; gap?: Measure }>`
  overflow: auto;
  flex-grow: ${p => p.flexGrow && (typeof p.flexGrow === 'boolean' ? 1 : p.flexGrow)};
  gap: ${p => p.gap && fixMeasure(p.gap)};
`;

export const InlineDiv = styled.div<{ gap?: Measure; justify?: CSSProperties['justifyContent'] }>`
  display: inline-flex;
  flex-direction: row;
  justify-content: ${p => p.justify};
  align-items: center;
  gap: ${p => p.gap && fixMeasure(p.gap)};
`;

const GripperIcon = styled(FontIcon).attrs({ iconName: 'GripperBarHorizontal' })`
  :hover {
    cursor: grab;
  }
`;

export const Gripper: FC = () => (
    <FlexDiv justify="center" align="center" height="100%">
        <GripperIcon />
    </FlexDiv>
);

interface INoOverflowTextProps {
    tooltip?: string;
    wrap?: boolean;
}

export const NoOverflowText = styled.span.attrs(({ children, tooltip }: { children: JSX.Element } & INoOverflowTextProps) => ({ title: tooltip ?? children })) <INoOverflowTextProps>`
  flex-shrink: 1 !important;
  min-width: 1px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${p => p.wrap ? undefined : 'nowrap'};
`;

export const FlexColumnDiv = styled.div<{ gap?: Measure }>`
  display: flex;
  flex-direction: column;
  gap: ${p => p.gap && fixMeasure(p.gap)};
`;

// copy of the built in FluentUI field validation style
export const ValidationMessage = styled.p<{ textAlign?: CSSProperties['textAlign'] }>`
  animation-name: css-0, css-13;
  animation-duration: 0.367s;
  animation-timing-function: cubic-bezier(0.1, 0.9, 0.2, 1);
  animation-fill-mode: both;

  font-size: 12px;
  font-weight: 400;
  margin: 0px;
  padding-top: 5px;
  text-align: ${p => p.textAlign};
`;

export const ErrorMessage = styled(ValidationMessage)`
  color: ${p => p.theme.semanticColors.errorText};
`;

export const WarningMessage = styled(ValidationMessage)`
  color: ${p => p.theme.semanticColors.warningText};
`;

export const DropdownWithIcons: FC<Omit<IDropdownProps, 'onRenderOption' | 'onRenderTitle'>> = (props) => (
    <Dropdown
        {...props}
        onRenderOption={(option, defaultRender) => (
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '0.5rem' }}>
                {
                    option && option.data && option.data.icon &&
                    <FontIcon iconName={option.data.icon} />
                }
                {defaultRender?.(option)}
            </Stack>
        )}
        onRenderTitle={(options, defaultRender) => (
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '0.5rem' }}>
                {
                    options && options[0].data && options[0].data.icon &&
                    <FontIcon iconName={options[0].data.icon} />
                }
                <span>{defaultRender?.(options)}</span>
            </Stack>
        )}
    />
);

export const LabelWithHint: FC<{ hint?: ITooltipHostProps['content'] } & ILabelProps> = (props) => (
    <InlineDiv gap="0.5rem">
        <Label {...props} />
        {
            props.hint &&
            <TooltipHost content={props.hint} styles={{ root: { display: 'flex', cursor: 'help !important' } }}>
                <FontIcon iconName="Info" />
            </TooltipHost>
        }
    </InlineDiv>
);